/**
 * Collapsible — shared disclosure primitive.
 *
 * Mirrors the card aesthetic and disclosure semantics from ProvenancePanel
 * (border, radius, 44px touch target, chevron, aria-expanded) without
 * touching ProvenancePanel itself.
 *
 * The outer <section aria-labelledby={buttonId}> provides the ARIA "region"
 * landmark. The <h2><button> wrapper follows the ARIA APG accordion pattern
 * so the button also registers as a heading in the document outline.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No navigator.onLine, no localStorage.
 *   - No Web APIs without a Capacitor equivalent.
 */

import { F } from '@/design/fonts';
import { RADIUS, SHADOW, SPACE, T, TRANSITION } from '@/design/tokens';
import { ChevronDown } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { useId, useState } from 'react';

export interface CollapsibleProps {
  title: string;
  /** When provided, renders "(count)" after the title in the button,
   *  or "None" when count is 0. */
  count?: number;
  /** Whether the body starts open. Defaults to false. */
  defaultOpen?: boolean;
  children: ReactNode;
  /**
   * Stable string prefix used to build the button id and content id.
   * When omitted, React's useId() generates a unique prefix.
   * Pass this when an external element needs to wire aria-controls to the button.
   */
  id?: string;
  /**
   * When true, the left edge uses the 3px pink accent border (trust / brand moment).
   * When false (default), the section uses a plain 1px border all around.
   */
  accent?: boolean;
  /**
   * Optional icon rendered muted before the title text inside the header button.
   * Intended for Lucide icons at size 14.
   */
  leadingIcon?: ReactNode;
  /**
   * Optional content rendered right-aligned in the header button, immediately
   * before the chevron. Use for compact summary widgets (e.g. ImpactGauge).
   * Must not contain interactive elements — it lives inside the toggle button.
   */
  headerAccessory?: ReactNode;
  /**
   * Which heading level to use for the APG accordion wrapper. Defaults to 2.
   * Use 3 when the Collapsible is nested inside an h2 section.
   */
  headingLevel?: 2 | 3;
}

export function Collapsible({
  title,
  count,
  defaultOpen = false,
  children,
  id,
  accent = false,
  leadingIcon,
  headerAccessory,
  headingLevel = 2,
}: CollapsibleProps) {
  const innerId = useId();
  const prefix = id ?? innerId;
  const buttonId = `${prefix}-btn`;
  const contentId = `${prefix}-content`;

  const [open, setOpen] = useState(defaultOpen);

  const sectionStyle: CSSProperties = accent
    ? {
        border: `1px solid ${T.border}`,
        borderLeft: `3px solid ${T.accent}`,
        borderRadius: RADIUS.md,
        background: T.card,
        overflow: 'hidden',
        boxShadow: SHADOW.card,
      }
    : {
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.md,
        background: T.card,
        overflow: 'hidden',
        boxShadow: SHADOW.card,
      };

  const headingStyle: CSSProperties = {
    margin: 0,
  };

  const buttonStyle: CSSProperties = {
    width: '100%',
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE.sm,
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    background: 'transparent',
    border: 'none',
    borderBottom: open ? `1px solid ${T.border}` : 'none',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: F.body,
    color: T.text,
  };

  const titleGroupStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.sm,
    fontWeight: 600,
    fontSize: 14,
    color: T.text,
    fontFamily: F.body,
  };

  const leadingIconStyle: CSSProperties = {
    color: T.textMuted,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  };

  const countStyle: CSSProperties = {
    fontFamily: F.mono,
    fontSize: 12,
    color: T.textMuted,
    fontWeight: 400,
  };

  const rightGroupStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.xs,
    flexShrink: 0,
  };

  const chevronStyle: CSSProperties = {
    color: T.textMuted,
    transition: `transform ${TRANSITION.fast}`,
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    flexShrink: 0,
  };

  const contentStyle: CSSProperties = {
    padding: SPACE.md,
  };

  // Resolve heading tag. Using a cast to avoid JSX factory needing generics.
  const HeadingTag = `h${headingLevel}` as 'h2' | 'h3';

  // Count badge: "None" for 0, "(n)" for positive values.
  const countBadge =
    count !== undefined ? (
      <span style={countStyle}>{count === 0 ? 'None' : `(${count})`}</span>
    ) : null;

  return (
    <section aria-labelledby={buttonId} style={sectionStyle}>
      {/* ARIA APG accordion pattern: heading wraps button so the button
          also participates in the document heading outline. */}
      <HeadingTag style={headingStyle}>
        <button
          id={buttonId}
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen((o) => !o)}
          style={buttonStyle}
        >
          <span style={titleGroupStyle}>
            {leadingIcon !== undefined && (
              <span style={leadingIconStyle} aria-hidden="true">
                {leadingIcon}
              </span>
            )}
            {title}
            {countBadge}
          </span>

          <span style={rightGroupStyle}>
            {headerAccessory}
            <ChevronDown size={14} aria-hidden="true" style={chevronStyle} />
          </span>
        </button>
      </HeadingTag>

      {/* Body — hidden attribute removes from a11y tree when closed.
          React omits the attr when value is false, so this is safe. */}
      <div id={contentId} hidden={!open} style={open ? contentStyle : undefined}>
        {children}
      </div>
    </section>
  );
}
